import React, { useEffect, useRef, forwardRef, useImperativeHandle, useState } from "react";
import Vditor from "vditor";
import "vditor/dist/index.css";
import styles from "./index.module.scss";
import LinkDialog from "./LinkDialog";
import RecordingNote from "components/shared/biz/MyNote/components/RecordingNote";
import MeetingNote from "components/shared/biz/MyNote/components/MeetingNote";

interface MarkdownEditorProps {
  defaultValue?: string;
  onChange?: (value: string) => void;
  height?: number | string;
  placeholder?: string;
  disabled?: boolean;
}

export interface MarkdownEditorRef {
  getValue: () => string;
  setValue: (value: string) => void;
  onInput?: (listener: (value: string) => void) => () => void;
}

const MarkdownEditor = forwardRef<MarkdownEditorRef, MarkdownEditorProps>(
  (
    {
      defaultValue = "",
      onChange,
      height = 500,
      placeholder = "Please enter content...",
      disabled = false,
    },
    ref
  ) => {
    const vditorRef = useRef<Vditor | null>(null);
    const containerIdRef = useRef(`vditor-${Date.now()}-${Math.random()}`);
    const isInitializing = useRef(false);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [selectedText, setSelectedText] = useState("");
    const selectionRangeRef = useRef<Range | null>(null);
    const [recordingNoteOpen, setRecordingNoteOpen] = useState(false);
    const [meetingNoteOpen, setMeetingNoteOpen] = useState(false);
    const openRecordingNoteRef = useRef<() => void>(() => {});
    const openMeetingNoteRef = useRef<() => void>(() => {});
    const inputListenersRef = useRef(new Set<(value: string) => void>());
    openRecordingNoteRef.current = () => setRecordingNoteOpen(true);
    openMeetingNoteRef.current = () => setMeetingNoteOpen(true);

    const notifyInput = (value: string) => {
      onChange?.(value);
      inputListenersRef.current.forEach((listener) => listener(value));
    };

    const registerInputListener = (listener: (value: string) => void) => {
      inputListenersRef.current.add(listener);
      return () => {
        inputListenersRef.current.delete(listener);
      };
    };

    useImperativeHandle(ref, () => ({
      getValue: () => {
        return vditorRef.current?.getValue().trim() || "";
      },
      setValue: (value: string) => {
        vditorRef.current?.setValue(value);
      },
      onInput: registerInputListener,
    }));

    useEffect(() => {
      if (isInitializing.current || vditorRef.current) {
        return;
      }

      const container = document.getElementById(containerIdRef.current);
      if (!container) {
        return;
      }

      isInitializing.current = true;

      const vditor = new Vditor(containerIdRef.current, {
        height,
        mode: "wysiwyg",
        lang: "en_US",
        placeholder,
        value: defaultValue,
        typewriterMode: false,
        cache: {
          enable: false,
        },
        input: (value: string) => {
          notifyInput(value);
        },
        toolbarConfig: {
          pin: true,
        },
        toolbar: [
          "headings",
          "bold",
          "italic",
          "strike",
          // "link",
          {
            name: "custom-link",
            tip: "Link",
            icon: '<svg viewBox="0 0 24 24"><path d="M10.59 13.41c.41.39.41 1.03 0 1.42-.39.39-1.03.39-1.42 0a5.003 5.003 0 0 1 0-7.07l3.54-3.54a5.003 5.003 0 0 1 7.07 0 5.003 5.003 0 0 1 0 7.07l-1.49 1.49c.01-.82-.12-1.64-.4-2.42l.47-.48a2.982 2.982 0 0 0 0-4.24 2.982 2.982 0 0 0-4.24 0l-3.53 3.53a2.982 2.982 0 0 0 0 4.24zm2.82-4.24c.39-.39 1.03-.39 1.42 0a5.003 5.003 0 0 1 0 7.07l-3.54 3.54a5.003 5.003 0 0 1-7.07 0 5.003 5.003 0 0 1 0-7.07l1.49-1.49c-.01.82.12 1.64.4 2.43l-.47.47a2.982 2.982 0 0 0 0 4.24 2.982 2.982 0 0 0 4.24 0l3.53-3.53a2.982 2.982 0 0 0 0-4.24.973.973 0 0 1 0-1.42z"></path></svg>',
            click: () => {
              const text = vditor.getSelection().trim();
              setSelectedText(text);
              const selection = window.getSelection();
              if (selection && selection.rangeCount > 0) {
                selectionRangeRef.current = selection.getRangeAt(0).cloneRange();
              }

              setLinkDialogOpen(true);
            },
          },
          "|",
          "list",
          "ordered-list",
          "|",
          {
            name: "add-webex-recording",
            tip: "Add Webex meeting or recording note",
            icon: '<svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><title>Webex</title><path d="M21.78 7.376c0.512 1.181 0.032 2.644 -1.11 3.106 -2.157 0.888 -3 -1.295 -3 -1.295 -0.236 -0.55 -0.727 -1.496 -1.335 -1.496 -0.204 0 -0.503 0 -0.94 0.844 -0.229 0.443 -0.434 1.185 -0.616 1.84l-0.09 0.32c-0.373 -1.587 -0.821 -3.454 -1.536 -4.816 -0.195 -0.38 -0.42 -0.74 -0.673 -1.08a5.135 5.135 0 0 1 1.743 -1.337 4.891 4.891 0 0 1 2.112 -0.463c1.045 0 2.765 0.338 4.227 2.227 0.167 0.206 0.317 0.424 0.448 0.654 0.278 0.441 0.52 0.904 0.726 1.383l0.043 0.113zM0.02 8.4C-0.15 7.105 0.8 5.845 1.953 5.755c1.794 -0.157 2.36 1.385 2.455 1.89l0.022 0.137c0.07 0.44 0.29 1.838 0.48 2.744 0.078 0.4 0.244 1.013 0.353 1.416l0.006 0.022 0.026 0.092c0.11 0.4 0.232 0.799 0.362 1.193 0.185 0.548 0.399 1.085 0.641 1.61 0.47 0.955 0.93 1.45 1.367 1.45 0.203 0 0.512 0 0.96 -0.878 0.283 -0.59 0.512 -1.208 0.684 -1.845 0.373 1.598 0.811 3.128 1.495 4.456 0.205 0.406 0.444 0.794 0.715 1.16a5.124 5.124 0 0 1 -1.742 1.338 4.88 4.88 0 0 1 -2.112 0.461c-1.548 0 -3.727 -0.698 -5.339 -4.005a22.407 22.407 0 0 1 -1.078 -2.824 26.848 26.848 0 0 1 -0.693 -2.656 48.56 48.56 0 0 1 -0.215 -1.114C0.191 9.603 0.074 8.872 0.02 8.4zm22.047 -2.645 -0.202 -0.022h-0.052c0.222 0.392 0.421 0.797 0.597 1.215l0.053 0.113c0.322 0.76 0.346 1.614 0.068 2.391a3.079 3.079 0 0 1 -1.552 1.749 2.93 2.93 0 0 1 -1.228 0.28 3.115 3.115 0 0 1 -0.854 -0.135c-0.299 1.182 -0.768 2.634 -1.195 3.511 -0.427 0.877 -0.93 1.451 -1.378 1.451 -0.192 0 -0.501 0 -0.95 -0.877a10.746 10.746 0 0 1 -0.683 -1.845 38.722 38.722 0 0 1 -0.396 -1.575 12.67 12.67 0 0 1 -0.136 -0.598l-0.002 -0.01c-0.406 -1.778 -0.865 -3.645 -1.655 -5.142A8.263 8.263 0 0 0 11.52 4.8a5.136 5.136 0 0 0 -1.748 -1.34A4.892 4.892 0 0 0 7.654 3C6.618 3 4.9 3.338 3.437 5.228c0.466 0.223 0.867 0.562 1.164 0.984 0.305 0.433 0.499 0.933 0.565 1.458 0.076 0.563 0.256 1.654 0.47 2.688l0.001 0.007c0.021 0.11 0.042 0.221 0.073 0.342 0.126 -0.34 0.25 -0.642 0.38 -0.955l0.112 -0.271 0.128 -0.293c0.235 -0.55 0.726 -1.496 1.324 -1.496 0.213 0 0.513 0 0.95 0.844 0.296 0.606 0.532 1.239 0.706 1.89 0.138 0.507 0.276 1.047 0.394 1.587 0.04 0.148 0.07 0.296 0.101 0.444l0.006 0.028c0.427 1.879 0.875 3.69 1.644 5.187 0.159 0.317 0.34 0.622 0.545 0.911 0.15 0.215 0.31 0.422 0.48 0.62 1.27 1.45 2.733 1.8 3.843 1.8 1.548 0 3.738 -0.698 5.35 -4.006 0.822 -1.7 1.515 -4.208 1.772 -5.48 0.256 -1.27 0.449 -2.419 0.534 -3.115 0.04 -0.307 0.023 -0.618 -0.051 -0.918 -0.075 -0.299 -0.205 -0.579 -0.382 -0.825a2.247 2.247 0 0 0 -0.653 -0.607 2.143 2.143 0 0 0 -0.826 -0.296z" fill="#0080A8"/></svg>',
            click: (event: any) => {
              const MENU_ID = "vditor-webex-dropdown";
              const existing = document.getElementById(MENU_ID);
              if (existing) {
                existing.remove();
                return;
              }
              const el = event?.target as HTMLElement;
              const buttonEl = (el?.closest?.("button") as HTMLElement) ?? el;
              const rect = buttonEl?.getBoundingClientRect?.();
              const menu = document.createElement("div");
              menu.id = MENU_ID;
              Object.assign(menu.style, {
                position: "fixed",
                zIndex: "9999",
                background: "#fff",
                border: "1px solid #DEDEDE",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                minWidth: "200px",
                top: rect ? `${rect.bottom + 4}px` : "60px",
                left: rect ? `${rect.left}px` : "100px",
              });
              const items: { label: string; action: () => void }[] = [
                {
                  label: "Add Meeting Summary & Transcript Note",
                  action: () => openMeetingNoteRef.current(),
                },
                {
                  label: "Add Recording Note",
                  action: () => openRecordingNoteRef.current(),
                },
              ];
              items.forEach(({ label, action }) => {
                const item = document.createElement("div");
                item.textContent = label;
                Object.assign(item.style, {
                  padding: "8px 16px",
                  cursor: "pointer",
                  fontSize: "13px",
                  color: "#171B1F",
                  whiteSpace: "nowrap",
                });
                item.addEventListener("mouseenter", () => {
                  item.style.backgroundColor = "#F0FBFF";
                });
                item.addEventListener("mouseleave", () => {
                  item.style.backgroundColor = "";
                });
                item.addEventListener("click", (e) => {
                  e.stopPropagation();
                  menu.remove();
                  action();
                });
                menu.appendChild(item);
              });
              document.body.appendChild(menu);
              const closeMenu = (e: MouseEvent) => {
                if (!menu.contains(e.target as Node)) {
                  menu.remove();
                  document.removeEventListener("click", closeMenu, true);
                }
              };
              setTimeout(() => document.addEventListener("click", closeMenu, true), 0);
            },
          },
        ],
        customWysiwygToolbar: () => {},
        after: () => {
          vditorRef.current = vditor;
          isInitializing.current = false;
          if (disabled) {
            vditor.disabled();
          }
        },
      });

      return () => {
        vditorRef.current = null;
        isInitializing.current = false;
        inputListenersRef.current.clear();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (vditorRef.current) {
        if (disabled) {
          vditorRef.current.disabled();
        } else {
          vditorRef.current.enable();
        }
      }
    }, [disabled]);

    const handleLinkConfirm = (url: string, text: string) => {
      if (vditorRef.current && selectionRangeRef.current) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(selectionRangeRef.current);
          const range = selectionRangeRef.current;
          const fragment = range.cloneContents();
          const tempDiv = document.createElement("div");
          tempDiv.appendChild(fragment);
          const blockTags = ["P", "LI", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "DIV"];
          const blockElements = Array.from(tempDiv.children).filter((child) =>
            blockTags.includes(child.tagName)
          );
          range.deleteContents();
          let linkMarkdown = "";
          if (blockElements.length > 1) {
            const texts = blockElements.map((el) => el.textContent?.trim() || "");
            linkMarkdown = texts
              .filter((t) => t)
              .map((t) => `[${t}](${url})`)
              .join("\n\n");
          } else if (selectedText.includes("\n")) {
            const lines = selectedText.split("\n").filter((line) => line.trim());
            linkMarkdown = lines.map((line) => `[${line.trim()}](${url})`).join("\n\n");
          } else {
            const finalText = text || selectedText || url;
            linkMarkdown = `[${finalText}](${url})`;
          }
          vditorRef.current.insertValue(linkMarkdown);
        }
        selectionRangeRef.current = null;
      } else if (vditorRef.current) {
        const linkMarkdown = `[${text || url}](${url})`;
        vditorRef.current.insertValue(linkMarkdown);
      }
    };

    const handleRecordingReplace = (text: string) => {
      vditorRef.current?.setValue(text);
      setRecordingNoteOpen(false);
    };

    const handleRecordingInsert = (text: string) => {
      vditorRef.current?.insertValue(text);
      setRecordingNoteOpen(false);
    };

    const handleMeetingReplace = (text: string) => {
      vditorRef.current?.setValue(text);
      setMeetingNoteOpen(false);
    };

    const handleMeetingInsert = (text: string) => {
      vditorRef.current?.insertValue(text);
      setMeetingNoteOpen(false);
    };

    return (
      <div className={styles.customVditorContainer}>
        <div id={containerIdRef.current} />
        <LinkDialog
          open={linkDialogOpen}
          initialUrl="https://"
          initialText={selectedText}
          onClose={() => setLinkDialogOpen(false)}
          onConfirm={handleLinkConfirm}
        />
        <RecordingNote
          open={recordingNoteOpen}
          onClose={() => setRecordingNoteOpen(false)}
          onReplace={handleRecordingReplace}
          onInsert={handleRecordingInsert}
        />
        <MeetingNote
          open={meetingNoteOpen}
          onClose={() => setMeetingNoteOpen(false)}
          onReplace={handleMeetingReplace}
          onInsert={handleMeetingInsert}
        />
      </div>
    );
  }
);

MarkdownEditor.displayName = "MarkdownEditor";

export default MarkdownEditor;
